import type { EdgeSignals, ServerProbeSignals, ServerSignals } from '@fraudeai/schema';
import { ReputationPanel } from './ReputationPanel.js';

/**
 * Что сервер увидел сам.
 *
 * Отдельная секция нужна, потому что это единственный слой, которому можно
 * доверять без оговорок: заголовки запроса и адрес сокета формирует сетевой
 * стек браузера, а не страница. Всё остальное в отчёте так или иначе пришло
 * из JavaScript.
 */

const IP_KIND_LABELS: Record<string, string> = {
  public: 'публичный адрес',
  private: 'адрес локальной сети',
  loopback: 'та же машина',
  cgnat: 'операторский NAT',
  linklocal: 'канальный уровень',
  unknown: 'не определён',
};

/**
 * Отпечатки рукопожатия TLS и кадров HTTP/2.
 *
 * Показывается отдельным блоком намеренно: это единственный слой, до которого
 * страница не дотягивается вообще ничем. Подменить `navigator.userAgent`
 * можно одной строкой, а набор расширений в ClientHello задаёт сетевая
 * библиотека браузера — туда из JavaScript хода нет.
 */
function EdgePanel({ edge }: { edge: EdgeSignals }) {
  const rows: [string, string][] = [
    ['Протокол', `${edge.negotiated} (предложено: ${edge.alpnOffered.join(', ') || '—'})`],
    ['Версия TLS', edge.tlsVersion || '—'],
    ['JA4', edge.ja4 || '—'],
    ['JA3', edge.ja3Hash || '—'],
    ['Шифров и расширений', `${edge.cipherCount} / ${edge.extensionCount}`],
    [
      'GREASE в рукопожатии',
      edge.hasGrease
        ? 'есть — так делают браузеры'
        : 'нет — так делают HTTP-библиотеки, но не браузеры',
    ],
  ];

  if (edge.h2Fingerprint) {
    rows.push(['Отпечаток HTTP/2', edge.h2Fingerprint]);
    rows.push(['Порядок псевдо-заголовков', edge.h2PseudoOrder ?? '—']);
  }

  return (
    <section>
      <h3>Отпечаток соединения</h3>
      <p className="hint">
        Порядок расширений TLS и значения кадров HTTP/2 задаёт сетевая библиотека внутри
        браузера. Из JavaScript туда хода нет ни у страницы, ни у расширения — поэтому
        подделать этот слой, не подменив сам браузер, невозможно.
      </p>

      <div className="scroll-x">
        <table>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td className="name" style={{ width: '34%' }}>
                  {label}
                </td>
                <td style={label === 'GREASE в рукопожатии' && !edge.hasGrease
                  ? { color: 'var(--fraudulent)' }
                  : undefined}>
                  {value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edge.ja4Raw && (
        <details className="explorer" style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-dim)' }}>
            Из чего сложился JA4
          </summary>
          <pre style={{ marginTop: 8 }}>{edge.ja4Raw.split('_').join('\n')}</pre>
        </details>
      )}
    </section>
  );
}

/**
 * Сверка адресов между транспортами.
 *
 * Каждая строка — с какого адреса клиент пришёл по своему транспорту.
 * Совпадение ничего не доказывает, а расхождение доказывает много: значит,
 * туннель пропускает не весь трафик, и часть его уходит напрямую.
 */
function ProbePanel({ probe }: { probe: ServerProbeSignals }) {
  const rows: { label: string; value: string; leaked: boolean }[] = [
    { label: 'HTTP (TCP)', value: probe.tcpIp, leaked: false },
  ];

  const differs = (ip: string): boolean => {
    const a = probe.tcpIp.split('.');
    const b = ip.split('.');
    // Грубое сравнение сети: у крупных операторов адрес плавает внутри пула,
    // и соседние адреса одного блока утечкой не являются.
    if (a.length !== 4 || b.length !== 4) return false;
    return a[0] !== b[0] || a[1] !== b[1] || a[2] !== b[2];
  };

  if (probe.stun) {
    rows.push({
      label: 'WebRTC (UDP)',
      value: `${probe.stun.ip}${probe.stun.port ? `:${probe.stun.port}` : ''}`,
      leaked: differs(probe.stun.ip),
    });
  }
  if (probe.http3) {
    rows.push({ label: 'HTTP/3 (QUIC)', value: probe.http3.ip, leaked: differs(probe.http3.ip) });
  }
  for (const resolver of probe.dns ?? []) {
    rows.push({ label: 'Резолвер DNS', value: resolver.ip, leaked: differs(resolver.ip) });
  }

  const notRun = (probe.offered ?? []).filter((kind) => {
    if (kind === 'stun') return !probe.stun;
    if (kind === 'http3') return !probe.http3;
    if (kind === 'dns') return (probe.dns ?? []).length === 0;
    return false;
  });

  return (
    <section>
      <h3>Откуда приходит трафик</h3>
      <p className="hint">
        Прокси и VPN часто туннелируют только TCP: страница идёт через туннель, а WebRTC,
        QUIC и запросы к DNS уходят напрямую и раскрывают настоящий адрес. Каждую строку
        наблюдала наша служба на своём сокете — со страницы эти значения подделать нельзя.
      </p>

      <div className="scroll-x">
        <table>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.label}-${index}`}>
                <td className="name" style={{ width: '34%' }}>
                  {row.label}
                </td>
                <td style={row.leaked ? { color: 'var(--fraudulent)' } : undefined}>
                  {row.value}
                  {row.leaked && ' — мимо туннеля'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {notRun.length > 0 && (
        <p className="hint" style={{ marginTop: 10 }}>
          Не дошли до сервера: {notRun.join(', ')}. Такое бывает при закрытом UDP,
          отключённом WebRTC, заблокированном QUIC или нестандартном DNS-пути.
          Эти пропуски снижают покрытие сети и считаются сетевым риск-сигналом,
          если соответствующая проба была предложена.
        </p>
      )}
    </section>
  );
}

export function ServerPanel({ server }: { server: ServerSignals }) {
  const rows: [string, string][] = [
    ['Адрес подключения', `${server.remoteIp} — ${IP_KIND_LABELS[server.ipKind] ?? server.ipKind}`],
    ['Протокол', `${server.httpVersion}${server.tls ? ' по TLS' : ' без TLS'}`],
    ['User-Agent в запросе', server.userAgent || '—'],
    [
      'Браузер по заголовку',
      server.uaParsed
        ? `${server.uaParsed.browser} ${server.uaParsed.browserVersion} · ${server.uaParsed.engine} · ${server.uaParsed.os}`
        : 'не разобран',
    ],
    ['Accept-Language', server.acceptLanguage || '—'],
    ['Accept-Encoding', server.acceptEncoding || '—'],
    ['Sec-CH-UA', server.secChUa || 'не отправлен'],
    ['Sec-CH-UA-Platform', server.secChUaPlatform || 'не отправлен'],
    [
      'Sec-Fetch',
      [server.secFetchSite, server.secFetchMode, server.secFetchDest].filter(Boolean).join(' / ') ||
        'не отправлены',
    ],
  ];

  if (server.forwardingHeaders?.length) {
    rows.push(['Заголовки пересылки', server.forwardingHeaders.join(', ')]);
  }
  if (server.claimedClientIp) {
    rows.push(['Адрес, заявленный посредником', server.claimedClientIp]);
  }

  return (
    <>
    {server.edge && <EdgePanel edge={server.edge} />}
    {server.reputation && <ReputationPanel reputation={server.reputation} />}
    {server.probe && <ProbePanel probe={server.probe} />}
    <section>
      <h3>Что видит сервер</h3>
      <p className="hint">
        Этот слой страница не контролирует: заголовки запроса и адрес соединения формирует
        сетевой стек браузера. Поэтому расхождение с тем, что сообщает JavaScript, —
        самая надёжная улика из доступных.
      </p>

      <div className="scroll-x">
        <table>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td className="name" style={{ width: '34%' }}>
                  {label}
                </td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 18 }}>
        <h3 style={{ fontSize: 13 }}>Порядок заголовков</h3>
        {server.headerOrderCaptured && server.headerOrder ? (
          <>
            <p className="hint">
              Каждый браузерный движок отправляет заголовки в своём устойчивом порядке.
              Подделать его из JavaScript нельзя — это свойство сетевого стека.
            </p>
            <div className="scroll-x">
              <ol
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                }}
              >
                {server.headerOrder.map((name, index) => (
                  <li
                    key={`${name}-${index}`}
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 12,
                      padding: '4px 9px',
                      borderRadius: 5,
                      border: '1px solid var(--border-strong)',
                      color: 'var(--text-dim)',
                    }}
                  >
                    <span style={{ color: 'var(--text-faint)' }}>{index + 1}.</span> {name}
                  </li>
                ))}
              </ol>
            </div>
          </>
        ) : (
          <p className="hint">
            Порядок снять не удалось: {server.headerOrderReason ?? 'причина не указана'}.
          </p>
        )}
      </div>
    </section>
    </>
  );
}
